import useClassNames from '@medflex/hooks/useClassNames';
import useIsExtraSmallScreen from '@medflex/hooks/useIsExtraSmallScreen';
import useDisableRubberBandScrolling from '@medflex/hooks/useDisableRubberBandScrolling';
import isMobileDeviceOrApp from '@medflex/utils/isMobileDeviceOrApp';
import { trackEvent } from '@medflex/utils/Tracking';
import PropTypes from 'prop-types';
import React from 'react';
import Collapsed from './Collapsed';
import Expanded from './Expanded';

const PhysicianNavBar = ({
  className,
  unreadMessages,
  chatPath,
  onActionCallback,
  isCollapsed,
  numScheduledCalls,
  invitationRequestsCount,
  unreadInvitationRequestCount,
  physiciansCount,
  patientsCount,
  onClickOutside = () => null,
  patientsShown,
  physiciansShown,
  invitationRequestsShown,
  configShown,
  getQueryString,
  sessionChatCount,
  staffCount,
  userPreview,
  inboxInMenuCollapsed,
  inboxInMenuExpanded,
}) => {
  const isExtraSmallScreen = useIsExtraSmallScreen();
  const CLASSES_COLLAPSED = `px-1 border-r ${isMobileDeviceOrApp() ? '' : 'sm:w-16'}`;
  const CLASSES_EXPANDED = `h-full border-r overflow-auto scrolling-touch relative shadow-none bg-white ${
    isMobileDeviceOrApp() ? '' : 'xl:w-1/4 sm:absolute lg:relative sm:shadow-xl lg:shadow-none'
  }`;
  const CLASSES_GENERAL = `w-full flex flex-col flex-shrink-0 border-t pb-2 pt-2 z-20 ${
    isMobileDeviceOrApp() ? '' : 'sm:border-t-0 sm:pb-0 sm:pt-4 sm:max-w-xs'
  }`;

  const cn = useClassNames(
    () => [
      CLASSES_GENERAL,
      !isCollapsed && CLASSES_EXPANDED,
      isCollapsed && CLASSES_COLLAPSED,
      className,
    ],
    [className, isCollapsed],
  );

  const navBarBackdropRef = React.useRef();

  const onClickOutsideHandler = event => {
    if (onClickOutside && event.target === navBarBackdropRef.current) {
      onClickOutside();
      trackEvent(event, {
        zone: 'Arzt_Hauptnavi',
        elementType: 'document',
        actionText: 'document',
      });
    }
  };

  React.useEffect(() => {
    document.addEventListener('click', onClickOutsideHandler, { capture: true });
    return () => {
      document.removeEventListener('click', onClickOutsideHandler, { capture: true });
    };
  }, []);

  const navRef = useDisableRubberBandScrolling();

  return (
    <nav data-component="PhysicianNavBar" className={cn} ref={navRef}>
      {isCollapsed ? (
        <div
          className={`flex flex-1 ${
            isExtraSmallScreen || isMobileDeviceOrApp()
              ? 'flex-row justify-around'
              : 'flex-col items-center'
          }`}
        >
          <Collapsed
            unreadMessages={unreadMessages}
            onActionCallback={onActionCallback}
            chatPath={chatPath}
            getQueryString={getQueryString}
            unreadInvitationRequestCount={unreadInvitationRequestCount}
            inboxInMenuCollapsed={inboxInMenuCollapsed}
          />
        </div>
      ) : (
        <div className="flex flex-1 flex-col">
          {userPreview}
          <Expanded
            unreadMessages={unreadMessages}
            onActionCallback={onActionCallback}
            invitationRequestsCount={invitationRequestsCount}
            unreadInvitationRequestCount={unreadInvitationRequestCount}
            physiciansCount={physiciansCount}
            patientsCount={patientsCount}
            sessionChatCount={sessionChatCount}
            chatPath={chatPath}
            numScheduledCalls={numScheduledCalls}
            staffCount={staffCount}
            configShown={configShown}
            patientsShown={patientsShown}
            physiciansShown={physiciansShown}
            invitationRequestsShown={invitationRequestsShown}
            getQueryString={getQueryString}
            inboxInMenuExpanded={inboxInMenuExpanded}
          />
        </div>
      )}
    </nav>
  );
};

PhysicianNavBar.defaultProps = {
  isCollapsed: false,
  onClickOutside: () => {},
  onActionCallback: () => {},
  numScheduledCalls: 0,
  invitationRequestsCount: 0,
  getQueryString: () => '/',
  userPreview: null,
};

PhysicianNavBar.propTypes = {
  isCollapsed: PropTypes.bool,
  unreadMessages: PropTypes.number.isRequired,
  chatPath: PropTypes.string,
  numScheduledCalls: PropTypes.number,
  onClickOutside: PropTypes.func,
  invitationRequestsCount: PropTypes.number,
  unreadInvitationRequestCount: PropTypes.number,
  physiciansCount: PropTypes.number,
  patientsCount: PropTypes.number,
  sessionChatCount: PropTypes.number,
  staffCount: PropTypes.number,
  onActionCallback: PropTypes.func,
  className: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.func,
    PropTypes.array,
    PropTypes.object,
  ]),
  patientsShown: PropTypes.bool,
  physiciansShown: PropTypes.bool,
  invitationRequestsShown: PropTypes.bool,
  getQueryString: PropTypes.func,
  configShown: PropTypes.bool,
  userPreview: PropTypes.node,
  inboxInMenuCollapsed: PropTypes.node,
  inboxInMenuExpanded: PropTypes.node,
};

export default PhysicianNavBar;
